// HM-led job creation — pure helpers shared by the job form, the Jessica call page and the dashboard panel.
// The backend (app/services/job_intake_fields.py) is the source of truth; these only drive the UI.

export const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export async function intakeApi(path, { method = 'GET', body } = {}) {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}/api/job-intakes${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  let data = null;
  try { data = await res.json(); } catch (e) { data = null; }
  if (!res.ok) {
    const err = new Error((data && data.detail) || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return data;
}

export const STATUS_LABELS = {
  form: 'Form in progress',
  form_done: 'Ready for Jessica',
  call_in_progress: 'Call in progress',
  call_paused: 'Call paused',
  call_done: 'Call complete',
  generating: 'Preparing documents',
  ready_for_review: 'Ready for your review',
  approved: 'Approved',
  failed: 'Needs attention',
};

export const progressText = (p) => (p ? `${p.done} of ${p.total}` : '');

/** show_if: hidden unless every condition holds ("$employment_type" = field 3's saved answer, from ctx). */
export function isShown(input, value, ctx = {}) {
  if (!input.show_if) return true;
  return Object.entries(input.show_if).every(([k, allowed]) =>
    allowed.includes(k.startsWith('$') ? (ctx || {})[k.slice(1)] : (value || {})[k]));
}

/** An input is required when shown and flagged required, or when another input has a triggering value. */
export function isRequired(input, value, ctx = {}) {
  if (!isShown(input, value, ctx)) return false;
  if (input.required) return true;
  return Object.entries(input.required_if || {}).some(([k, allowed]) => allowed.includes((value || {})[k]));
}

const isEmpty = (v) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
const named = (v) => (v && typeof v === 'object' ? v.name : v);

/** First problem the HM must fix before this field can be saved as answered, or '' (server re-validates). */
export function localProblem(field, value, ctx = {}) {
  const v = value || {};
  if (field.key === 'compensation' && !(ctx || {}).employment_type) return 'Answer 3. Employment type first';
  for (const input of field.inputs) {
    if (!isShown(input, v, ctx)) continue;
    const raw = v[input.name];
    if (input.kind === 'skills') {
      const rows = (raw || []).filter((s) => s && String(s.name || '').trim());
      if (isRequired(input, v, ctx) && rows.length === 0) return `${input.label} is required`;
      if (rows.length > (input.max_items || 5)) return `At most ${input.max_items || 5} skills`;
      const noYears = rows.find((s) => isEmpty(s.years));
      if (noYears) return `Add the years for ${noYears.name}`;
    } else if (input.kind === 'chips') {
      if (isRequired(input, v, ctx) && !(raw || []).length) return `${input.label} is required`;
      if ((raw || []).length > (input.max_items || 5)) return `At most ${input.max_items || 5} skills`;
    } else if (input.kind === 'locations') {
      const rows = (raw || []).filter((l) => l && l.country);
      if (isRequired(input, v, ctx) && !rows.length) return 'Add at least one location';
    } else if (input.kind === 'tech_screen') {
      if (isRequired(input, v, ctx) && !(raw && raw.duration_minutes && raw.coding_mode)) return "Choose the technical screen's length and interview type";
    } else if (input.kind === 'interview_rounds') {
      const rows = raw || [];
      if (rows.length > (input.max_items || 5)) return `At most ${input.max_items || 5} rounds`;
      for (let i = 0; i < rows.length; i += 1) {
        if (!rows[i].type) return `Round ${i + 1}: choose the round type`;
        if (!rows[i].format) return `Round ${i + 1}: choose Video, On-site or Phone`;
        const words = String(rows[i].description || '').trim() ? String(rows[i].description).trim().split(/\s+/).length : 0;
        if (words > (input.description_words || 20)) return `Round ${i + 1}: keep the description to ${input.description_words || 20} words`;
      }
    } else if (input.kind === 'department' || input.kind === 'reports_to') {
      if (isRequired(input, v, ctx) && isEmpty(String(named(raw) || '').trim())) return `${input.label} is required`;
    } else if (isRequired(input, v, ctx) && isEmpty(typeof raw === 'string' ? raw.trim() : raw)) {
      return `${input.label} is required`;
    }
  }
  if (field.key === 'schedule' && v.hours_from && v.hours_from === v.hours_to) return "From and To can't be the same";
  return '';
}

/** A saved answer → the form's draft (department / reports-to back to {id, name} pairs). */
export function draftFromValue(fieldKey, value) {
  const v = { ...(value || {}) };
  if (fieldKey === 'dept_manager') {
    if (typeof v.department === 'string') v.department = { id: v.department_id ?? null, name: v.department };
    if (typeof v.reporting_manager === 'string') v.reporting_manager = { user_id: v.reporting_manager_user_id ?? null, name: v.reporting_manager };
  }
  return v;
}

/** The form's sections (the call fields are asked by Jessica, not shown on the form). */
export function sectionsWithFields(spec) {
  if (!spec) return [];
  return spec.sections.map((s) => ({ ...s, fields: spec.fields.filter((f) => f.section === s.key && !f.call_field) }))
    .filter((s) => s.fields.length);
}

export const fieldDone = (answer) => !!answer && (answer.status === 'answered' || answer.status === 'tbd');

/** Index of the first field not answered / TBD (or 0 when all are done). */
export function firstOpenIndex(spec, answers) {
  if (!spec) return 0;
  const i = spec.fields.findIndex((f) => !f.call_field && !fieldDone((answers || {})[f.key]));
  return i < 0 ? 0 : i;
}

export const canTbd = (field) => field.tbd_allowed !== false;

// ElevenLabs refused the call for capacity (concurrency limit / 429 / "try again later" close code 1013).
export const BUSY_MESSAGE = 'Jessica is busy, try again in a few minutes.';
export function isBusyError(e) {
  if (!e) return false;
  if (e.closeCode === 1013 || e.status === 429 || e.status === 503) return true;
  const text = [e.message, e.closeReason, e.reason].filter(Boolean).join(' ');
  return /concurren|too many (concurrent )?(calls|conversations|requests|sessions)|rate.?limit|capacity|busy|\b429\b/i.test(text);
}
